import { useEffect, useRef } from 'react';

export default function CompanyPage() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('.scroll-animate').forEach((el) => observerRef.current?.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    // Update page title and meta tags
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    document.title = '会社概要 | 合同会社Lill - 東京都清瀬市のAI・IT企業';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '合同会社Lillの会社概要。東京都清瀬市に拠点を置き、AI導入支援、システム開発、HP制作などを提供しています。代表：小幡和希。2025年3月設立。');
    }

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', siteUrl + '/company');
    }

    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', '会社概要 | 合同会社Lill');
    }

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', '合同会社Lillの会社概要。東京都清瀬市に拠点を置き、AI導入支援、システム開発、HP制作などを提供しています。');
    }

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('href', siteUrl + '/company');
    }

    // Schema.org JSON-LD for Company page
    const schemaOrganization = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '合同会社Lill',
      url: siteUrl,
      logo: `${siteUrl}/logo.png`,
      description: 'テクノロジーで、ビジネスの"次"をつくる。AIとITの力で、事業を加速させるパートナー。',
      foundingDate: '2025-03',
      founder: {
        '@type': 'Person',
        name: '小幡 和希'
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '下宿2丁目439-3',
        addressLocality: '清瀬市',
        addressRegion: '東京都',
        postalCode: '204-0001',
        addressCountry: 'JP'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        availableLanguage: 'Japanese'
      }
    };

    const schemaLocalBusiness = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: '合同会社Lill',
      image: `${siteUrl}/logo.png`,
      '@id': siteUrl,
      url: siteUrl,
      telephone: '',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '下宿2丁目439-3',
        addressLocality: '清瀬市',
        addressRegion: '東京都',
        postalCode: '204-0001',
        addressCountry: 'JP'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 35.7858,
        longitude: 139.5267
      },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday'
        ],
        opens: '09:00',
        closes: '18:00'
      }
    };

    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(schemaOrganization);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(schemaLocalBusiness);
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* ── Hero ── */}
      <section className="relative py-20 sm:py-32 lg:py-40 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&h=800&fit=crop"
            alt="会社概要"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#1a2744]/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-4 sm:mb-6 tracking-tight">COMPANY</h1>
          <p className="text-xl sm:text-2xl lg:text-3xl text-white/90 tracking-wide">会社概要</p>
        </div>
      </section>

      {/* ── Vision & Mission ── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="scroll-animate">
            <p className="text-xs font-semibold tracking-widest text-[#2563EB] uppercase mb-4 text-center">Vision & Mission</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-12 sm:mb-16 lg:mb-20 text-center tracking-tight">
              ビジョン・ミッション
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
              <div className="bg-[#1a2744] rounded-lg p-8 sm:p-10 lg:p-12 text-white border border-gray-200">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-6 sm:mb-8">
                  <i className="ri-eye-line text-4xl sm:text-5xl" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 tracking-tight">Vision</h3>
                <p className="text-base sm:text-lg leading-relaxed opacity-95">
                  テクノロジーの恩恵を、すべてのビジネスに届ける。
                </p>
              </div>

              <div className="bg-[#1a2744] rounded-lg p-8 sm:p-10 lg:p-12 text-white border border-gray-200">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-6 sm:mb-8">
                  <i className="ri-compass-3-line text-4xl sm:text-5xl" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 tracking-tight">Mission</h3>
                <p className="text-base sm:text-lg leading-relaxed opacity-95">
                  最先端のAI・IT技術で、企業の課題解決と成長を加速させる。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Message ── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto">
          <div className="scroll-animate mb-12 sm:mb-16 lg:mb-20">
            <p className="text-xs font-semibold tracking-widest text-[#2563EB] uppercase mb-4 text-center">Message</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-8 sm:mb-12 text-center tracking-tight">代表挨拶</h2>

            <div className="bg-white rounded-lg p-8 sm:p-10 lg:p-12 border border-gray-200">
              <div className="mb-6 sm:mb-8">
                <div className="text-2xl sm:text-3xl font-bold text-[#1a2744] tracking-tight mb-2">小幡 和希</div>
                <div className="text-sm sm:text-base text-gray-600">代表</div>
              </div>
              <div className="space-y-4 sm:space-y-6">
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  "難しそう"を"かんたん"に変える。
                </p>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  AIとテクノロジーの力で、すべての企業がもっと自由に事業を運営できる世界を目指しています。お客様一人ひとりの課題に寄り添い、最適な解決策をご提案します。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Company Info ── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="scroll-animate">
            <p className="text-xs font-semibold tracking-widest text-[#2563EB] uppercase mb-4 text-center">Company Info</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-8 sm:mb-12 lg:mb-16 text-center tracking-tight">会社概要</h2>

            <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
              <table className="w-full">
                <tbody>
                  {[
                    { label: '会社名', value: '合同会社Lill' },
                    { label: '所在地', value: '東京都清瀬市下宿2丁目439-3' },
                    { label: '代表者', value: '小幡 和希' },
                    { label: '設立', value: '2025年3月' },
                    {
                      label: '事業内容',
                      value: (
                        <ul className="space-y-1">
                          <li className="text-sm sm:text-base text-gray-700">
                            ・AIアシスタントサービス「teal」の運営
                          </li>
                          {[
                            'AI活用設計・コンサル',
                            'ホームページ制作',
                            'システム設計・開発',
                            'IT設計・運用支援',
                            'web3事業立ち上げ/マーケティング支援',
                          ].map((item, idx) => (
                            <li key={idx} className="text-sm sm:text-base text-gray-700">
                              ・{item}
                            </li>
                          ))}
                        </ul>
                      ),
                    },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 font-bold text-[#1a2744] text-sm sm:text-base align-top w-32 sm:w-40 bg-[#F5F7FA]">
                        {row.label}
                      </td>
                      <td className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 text-gray-700 text-sm sm:text-base leading-relaxed">
                        {row.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
